import React, { memo } from 'react';
import {
  Box,
  Stack,
  TextField,
  IconButton,
  Chip,
  Autocomplete,
} from '@mui/material';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { colors } from '../constants';

/**
 * Multi-value barcode editor. Holds a list of barcode strings (an item can have
 * several - e.g. unit and case codes). Each barcode is shown as a chip INSIDE the
 * field; type a code and press Enter (or leave the field) to add it, click the
 * chip's x to remove it.
 * An optional scan button can push a scanned code.
 *
 * Memoized: the underlying Autocomplete is expensive, and on a form with many
 * line items editing one field must not re-render every other line's barcode
 * editor. Callers must therefore pass stable callbacks.
 */
function BarcodesInput({
  value = [],
  onChange,
  label = 'Barcodes',
  placeholder = 'Type a barcode and press Enter',
  onScan,
}) {
  const commit = (next) => {
    const cleaned = [];
    next.forEach((raw) => {
      const code = String(raw).trim();
      if (code && !cleaned.includes(code)) cleaned.push(code);
    });
    onChange(cleaned);
  };

  const field = (
    <Autocomplete
      multiple
      freeSolo
      // A code typed without pressing Enter is committed on blur; otherwise
      // clicking a submit button right after typing would drop it.
      autoSelect
      fullWidth
      size="small"
      options={[]}
      value={value}
      onChange={(_e, next) => commit(next)}
      renderTags={(vals, getTagProps) =>
        vals.map((option, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return <Chip key={key} label={option} size="small" {...tagProps} />;
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length ? '' : placeholder}
        />
      )}
    />
  );

  if (!onScan) return field;

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="flex-start">
        {field}
        <IconButton
          onClick={onScan}
          sx={{
            backgroundColor: colors.primary,
            color: '#fff',
            borderRadius: 1.5,
            '&:hover': { backgroundColor: colors.primaryDark },
          }}
        >
          <QrCodeScannerIcon />
        </IconButton>
      </Stack>
    </Box>
  );
}

export default memo(BarcodesInput);
